import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrg } from '../contexts/OrgContext';
import { useTheme } from '../contexts/ThemeContext';
import { safeGlobeReturn, startGlobeSession } from '../lib/globe';

/**
 * Sign-in hop for the 3D globe (docs/GLOBE_INTEGRATION.md). GIS-EYE sends a visitor with no
 * session to `GEV_LOGIN_URL?return=<globe URL>`; this page (behind the usual portal sign-in)
 * mints the globe session, plants its cookie and returns them. It also covers the portal's
 * "Globe" card, which cannot mint a GIS-EYE token itself.
 */
function GlobeLaunch() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { selectedOrgId, loading } = useOrg();
  const { theme } = useTheme();
  const [error, setError] = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (loading || started.current) return;
    started.current = true;
    (async () => {
      try {
        // A globe that rejects our cookie (secret mismatch) bounces straight back here:
        // stop after three hops in a minute instead of looping.
        const now = Date.now();
        let hops = [];
        try {
          hops = JSON.parse(sessionStorage.getItem('globe-launch-hops') || '[]').filter((t) => now - t < 60000);
          hops.push(now);
          sessionStorage.setItem('globe-launch-hops', JSON.stringify(hops));
        } catch {
          /* storage unavailable: no loop guard, the globe's own checks still apply */
        }
        if (hops.length > 3) {
          setError('The globe did not accept the sign-in. Ask an administrator to check GLOBE_JWT_SECRET and GEV_JWT_HS256_SECRET.');
          return;
        }
        const session = await startGlobeSession({ organizationId: selectedOrgId, theme });
        // Off geostack.com the cookie cannot reach the globe; sending the visitor on would loop
        // (the globe would bounce them straight back here).
        if (!session.cookieSet) {
          setError('Open the 3D globe from geostack.com: its sign-in cookie cannot be shared from this address.');
          return;
        }
        const back = safeGlobeReturn(params.get('return') || '', session.url);
        window.location.replace(back || session.url);
      } catch (err) {
        setError(
          err?.response?.status === 503
            ? 'The 3D globe is not set up on this deployment yet.'
            : 'Could not open the 3D globe. Try again from the sidebar.',
        );
      }
    })();
  }, [loading, selectedOrgId, theme, params]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
      {error ? (
        <>
          <p>{error}</p>
          <button type="button" onClick={() => navigate('/')}>Back to Ultimate-GIS</button>
        </>
      ) : (
        <p>Opening the 3D globe...</p>
      )}
    </div>
  );
}

export default GlobeLaunch;
